import React, { forwardRef, useEffect, useRef, useState, type ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { Fade, Toast } from 'react-bootstrap-5';

import { FontAwesomeIcon, type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faTriangleExclamation,
} from '@fortawesome/pro-solid-svg-icons';
import { SHOW_SNACKBAR_EVENT, type ShowSnackbarOptions, type SnackbarPosition, type SnackbarVariant } from './helper';

const VARIANT_ICONS: Record<SnackbarVariant, FontAwesomeIconProps['icon']> = {
  danger: faCircleExclamation,
  info: faCircleInfo,
  success: faCircleCheck,
  warning: faTriangleExclamation,
};

// Bootstrap's danger color is exposed as `destructive` in this app's theme,
// matching shared/snackbar so visuals stay consistent across toasts.
const colorClassFor = (variant?: SnackbarVariant) => variant === 'danger' ? 'destructive' : variant;

type ToastEntry = {
  id: number;
  show: boolean;
  text: React.ReactNode;
  variant?: SnackbarVariant;
  delay: number;
  autohide: boolean;
  position: SnackbarPosition;
  icon: FontAwesomeIconProps['icon'] | undefined;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ToastTransition = forwardRef<any, any>(({ children, ...props }, ref) => (
  <Fade {...props} appear mountOnEnter ref={ref} unmountOnExit>
    {children}
  </Fade>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
)) as ComponentType<any>;
ToastTransition.displayName = 'SnackbarToastTransition';

const containerClassName = (position: SnackbarPosition) => ['toast-container',
  'position-fixed',
  'bottom-0',
  'p-3',
  'd-flex',
  'flex-column',
  'gap-2',
  'z-top',
  position === 'right' ? 'end-0 align-items-end' : 'start-0 align-items-start',]
  .join(' ');

// Mount once per page. Multiple instances will each render every dispatched toast.
export default function Snackbar() {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const idRef = useRef(0);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<ShowSnackbarOptions>).detail;
      if (!detail) return;

      const variant = detail.variant ?? 'success';
      idRef.current += 1;

      setToasts(prev => [
        ...prev,
        {
          id: idRef.current,
          show: true,
          text: detail.text,
          variant,
          delay: detail.delay ?? 4000,
          autohide: detail.autohide ?? true,
          position: detail.position ?? 'right',
          icon: detail.icon ?? VARIANT_ICONS[variant],
        },
      ]);
    };

    window.addEventListener(SHOW_SNACKBAR_EVENT, handler);
    return () => window.removeEventListener(SHOW_SNACKBAR_EVENT, handler);
  }, []);

  const startClose = (id: number) => {
    setToasts(prev => prev.map(t => (t.id === id ? { ...t, show: false } : t)));
  };

  const removeEntry = (id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  if (typeof document === 'undefined' || toasts.length === 0) return null;

  const renderEntry = (entry: ToastEntry) => {
    const colorClass = colorClassFor(entry.variant);

    return (
      <Toast
        aria-label="notification"
        autohide={entry.autohide}
        className={['w-auto', colorClass ? `border border-${colorClass}` : null]
          .filter(Boolean)
          .join(' ')}
        delay={entry.delay}
        key={entry.id}
        show={entry.show}
        transition={ToastTransition}
        onClose={() => startClose(entry.id)}
        onExited={() => removeEntry(entry.id)}
      >
        <Toast.Body className="d-flex align-items-center gap-2 py-2 px-3">
          {entry.icon && (
            <FontAwesomeIcon
              className={colorClass ? `text-${colorClass}` : undefined}
              icon={entry.icon}
            />
          )}

          <div className="flex-grow-1">{entry.text}</div>

          <button
            aria-label="Close"
            className="btn-close"
            type="button"
            onClick={() => startClose(entry.id)}
          />
        </Toast.Body>
      </Toast>
    );
  };

  const leftToasts = toasts.filter(t => t.position === 'left');
  const rightToasts = toasts.filter(t => t.position === 'right');

  return (
    <>
      {leftToasts.length > 0 &&
        createPortal(
          <div className={containerClassName('left')}>
            {leftToasts.map(renderEntry)}
          </div>,
          document.body,
        )}
      {rightToasts.length > 0 &&
        createPortal(
          <div className={containerClassName('right')}>
            {rightToasts.map(renderEntry)}
          </div>,
          document.body,
        )}
    </>
  );
}
